import { z } from 'zod';

export const KycStatusEnum = z.enum(['NONE', 'PENDING', 'VERIFIED', 'REJECTED', 'REVIEW_MANUAL']);
export type KycStatus = z.infer<typeof KycStatusEnum>;
export const UserRoleEnum = z.enum(['USER', 'ADMIN', 'STAFF']);
export type UserRole = z.infer<typeof UserRoleEnum>;

export const UserListItem = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable().optional(),
  role: UserRoleEnum,
  kycStatus: KycStatusEnum,
  disabledAt: z.string().nullable(),
  // ko-admin#269 — sent on the list since ko-backend#985; optional so a list
  // from an older backend still parses.
  suspendedUntil: z.string().nullable().optional(),
  createdAt: z.string(),
  // A-03 / A-21 — agregados por usuario que el backend ahora expone tanto en el
  // listado (items[]) como en el detalle. GMV vendido en centavos (money.ts).
  gmvSoldCents: z.number(),
  tradesCount: z.number(),
  tournamentsCount: z.number(),
});
export type UserListItem = z.infer<typeof UserListItem>;

export const SessionItem = z.object({
  id: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  deviceId: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
});
export type SessionItem = z.infer<typeof SessionItem>;

export const AuditEntry = z.object({
  id: z.string(),
  action: z.string(),
  actorId: z.string().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  payload: z.unknown(),
  ip: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

/**
 * ko-backend#1128 (D10) — one module's tier benefits. Paused means the
 * commission is Tier 1's whatever level the user shows. `cause` says what can
 * give them back: a CHARGEBACK comes back on its own when its dispute is ruled
 * for the user; SANCTION and ADMIN only from this panel.
 */
export const BenefitsPauseCause = z.enum(['SANCTION', 'CHARGEBACK', 'ADMIN']);
export type BenefitsPauseCause = z.infer<typeof BenefitsPauseCause>;
export const TierBenefitsState = z.object({
  active: z.boolean(),
  cause: BenefitsPauseCause.nullable(),
  reason: z.string().nullable(),
  pausedAt: z.string().nullable(),
});
export type TierBenefitsState = z.infer<typeof TierBenefitsState>;
export const TIER_BENEFIT_MODULES = [
  'MARKETPLACE',
  'TRADER',
  'PARTICIPANT',
  'STAFF',
  'CREATOR',
] as const;
export type TierBenefitModule = (typeof TIER_BENEFIT_MODULES)[number];
// zod 4: `z.record` with enum keys requires EVERY key; the backend may send
// only some modules, which is what `partialRecord` accepts (zod 3's record).
export const TierBenefits = z.partialRecord(z.enum(TIER_BENEFIT_MODULES), TierBenefitsState);
export type TierBenefits = z.infer<typeof TierBenefits>;

/** Founder stores — one live `SellerCommissionGrant` of the user. */
export const CommissionGrant = z.object({
  id: z.string(),
  kind: z.string(),
  module: z.string(),
  ratePct: z.string(),
  reason: z.string(),
  grantedById: z.string(),
  grantedAt: z.string(),
  expiresAt: z.string().nullable(),
});
export type CommissionGrant = z.infer<typeof CommissionGrant>;

/** Body of `POST /admin/users/:id/benefits`; `reason` 10..500, shown to the user. */
export interface SetTierBenefitsBody {
  active: boolean;
  modules?: TierBenefitModule[];
  reason: string;
}
export const BENEFITS_REASON_MIN = 10;
export const BENEFITS_REASON_MAX = 500;

export const UserDetail = UserListItem.extend({
  emailVerifiedAt: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  bio: z.string().nullable(),
  locationCity: z.string().nullable(),
  locationCountry: z.string().nullable(),
  language: z.string().nullable(),
  kycVerifiedAt: z.string().nullable(),
  categoryInterests: z.array(z.string()),
  strikeCount: z.number(),
  updatedAt: z.string(),
  sessions: z.array(SessionItem),
  recentAudit: z.array(AuditEntry),
  // #84 — suspensión temporal. OJO: hoy `GET /admin/users/:id` NO devuelve este
  // campo (su `select` en admin-users.service.ts → detail() no lo incluye),
  // aunque la columna existe en el modelo User y sí viaja en la cola de
  // apelaciones. Se declara opcional para no romper el contrato actual y para
  // que la UI lo aproveche en cuanto el backend lo exponga; mientras sea
  // `undefined` el estado de suspensión es DESCONOCIDO en el detalle.
  suspendedUntil: z.string().nullable().optional(),
  // ko-backend#1128 (D10). Optional so a backend without it still parses; the
  // card then says it does not know rather than «all active».
  benefits: TierBenefits.optional(),
  /**
   * Founder stores — the live sealed commission rates, which override the
   * tier (and which a benefits pause overrides). Optional for a backend
   * without them.
   */
  commissionGrants: z.array(CommissionGrant).optional(),
});
export type UserDetail = z.infer<typeof UserDetail>;

export interface ListUsersQuery {
  q?: string;
  role?: UserRole;
  kycStatus?: KycStatus;
  disabled?: boolean;
  /** ko-admin#269 — a temporary suspension in force (ko-backend#985). */
  suspended?: boolean;
  limit?: number;
  cursor?: string;
}

// #22 — acción masiva sobre usuarios (POST /admin/users/bulk).
export const BulkActionEnum = z.enum(['BAN', 'DISABLE', 'FORCE_LOGOUT']);
export type BulkAction = z.infer<typeof BulkActionEnum>;

export interface BulkUsersBody {
  action: BulkAction;
  userIds: string[];
  reason: string;
}

// Respuesta: procesa por-usuario tolerando fallos individuales.
export const BulkUsersResult = z.object({
  done: z.number(),
  failed: z.array(z.object({ userId: z.string(), error: z.string() })),
});
export type BulkUsersResult = z.infer<typeof BulkUsersResult>;

// #84 — suspensión TEMPORAL del usuario (PATCH /admin/users/:id/suspension).
// `suspendedUntil`: fecha ISO FUTURA para suspender, `null` para levantar.
//
// #430 — `reason` ya SÍ viaja al backend y queda auditado. Antes el DTO no lo
// aceptaba, así que el panel lo pedía pero se lo guardaba: quien revisara una
// apelación no podía saber por qué se había suspendido a ese usuario.
// Obligatorio al suspender, opcional al levantar. (Contrato: SetSuspensionDto.)
export interface SetSuspensionBody {
  suspendedUntil: string | null;
  reason?: string;
}

export const SetSuspensionResult = z.object({
  id: z.string(),
  suspendedUntil: z.string().nullable(),
});
export type SetSuspensionResult = z.infer<typeof SetSuspensionResult>;

// #84 — cola de apelaciones DE SUSPENSIÓN (GET /admin/users/suspension-appeals).
// Distinta de la cola de apelaciones de disputa (features/disputes): otro
// recurso, otro enum de estado y sin paginación por cursor (devuelve {items}).
export const SuspensionAppealStatusEnum = z.enum(['OPEN', 'ACCEPTED', 'REJECTED']);
export type SuspensionAppealStatus = z.infer<typeof SuspensionAppealStatusEnum>;

export const SuspensionAppealItem = z.object({
  id: z.string(),
  reason: z.string(),
  status: SuspensionAppealStatusEnum,
  createdAt: z.string(),
  updatedAt: z.string(),
  // El backend incluye los datos mínimos del usuario para pintar la cola sin
  // un segundo round-trip; aquí sí llega `suspendedUntil`.
  user: z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string().nullable(),
    suspendedUntil: z.string().nullable(),
  }),
});
export type SuspensionAppealItem = z.infer<typeof SuspensionAppealItem>;

export const SuspensionAppealsResponse = z.object({
  items: z.array(SuspensionAppealItem),
});
export type SuspensionAppealsResponse = z.infer<typeof SuspensionAppealsResponse>;

export interface ListSuspensionAppealsQuery {
  status?: SuspensionAppealStatus;
  limit?: number;
}

// #432 — resolution of a suspension appeal (PATCH /admin/users/suspension-appeals/:id).
// `OPEN` is deliberately absent: the backend DTO only admits ACCEPTED/REJECTED,
// because reopening is not resolving. `reason` is MANDATORY (3–500 chars) and is
// what ends up in the audit payload — accepting also lifts the suspension in the
// same transaction, so the record of why is the only trace of that decision.
export const ResolveSuspensionAppealStatusEnum = z.enum(['ACCEPTED', 'REJECTED']);
export type ResolveSuspensionAppealStatus = z.infer<typeof ResolveSuspensionAppealStatusEnum>;

export interface ResolveSuspensionAppealBody {
  status: ResolveSuspensionAppealStatus;
  reason: string;
}

// `suspensionLifted` mirrors `status === 'ACCEPTED'` server-side: it is the
// backend telling us the user's `suspendedUntil` was cleared alongside the
// appeal, which is why resolving must invalidate the user detail too.
export const ResolveSuspensionAppealResult = z.object({
  id: z.string(),
  userId: z.string(),
  status: SuspensionAppealStatusEnum,
  suspensionLifted: z.boolean(),
});
export type ResolveSuspensionAppealResult = z.infer<typeof ResolveSuspensionAppealResult>;

// ── ko-backend#817 — the sanction ladder (PRD §16.3) ────────────────────────
//
// The seven rungs by NAME, in ladder order. The name is what the backend
// accepts on the wire (`ApplySanctionSchema`) and what the audit row shows; the
// ordinal comes back as `step` so nothing here has to keep a table of its own.
// Not to be confused with `SANCTION_TYPES` in `schemas/creators.ts`: that is
// the creator-profile sanction (warning / suspension / revocation), a
// different resource with different levers.
export const SANCTION_TIERS = [
  'WARNING',
  'REMOVE_ACTIVE_CONTENT',
  'REDUCE_VISIBILITY',
  'ECONOMIC_PENALTY',
  'TIER_REDUCTION',
  'TEMP_SUSPENSION',
  'PERMANENT_EXPULSION',
] as const;
export const SanctionTierEnum = z.enum(SANCTION_TIERS);
export type SanctionTier = z.infer<typeof SanctionTierEnum>;

// Bounds of `reason` in the backend DTO (`z.string().trim().min(10).max(500)`).
// Ten, not three: the reason lands verbatim on the audit row, the outbox event
// and the notification the user receives.
export const USER_SANCTION_REASON_MIN = 10;
export const USER_SANCTION_REASON_MAX = 500;

// Body of `POST /admin/users/:id/sanctions`. The backend schema is `.strict()`,
// so nothing the operator did not fill may travel — `sanctionKey` is only sent
// when given. It is the DOMAIN idempotency discriminator `(user, tier, key)`:
// absent, the backend mints one per call, so two warnings on two days are two
// sanctions.
export interface ApplyUserSanctionBody {
  tier: SanctionTier;
  reason: string;
  sanctionKey?: string;
}

// One lever's receipt. `detail` is per lever (`listingsArchived`,
// `sessionsRevoked`, `amount`…) and is read leniently on the display side: a
// lever this build does not know still has to be shown, by its name.
export const SanctionEffect = z.object({
  lever: z.string(),
  detail: z.record(z.string(), z.unknown()),
});
export type SanctionEffect = z.infer<typeof SanctionEffect>;

export const ApplyUserSanctionResult = z.object({
  // Id of the `sanction.applied` AuditEvent — null only if the row could not
  // be found back, which the backend tolerates rather than failing the call.
  sanctionId: z.string().nullable(),
  userId: z.string(),
  tier: SanctionTierEnum,
  step: z.number().int().min(1).max(7),
  // False on a replay of the same `(user, tier, sanctionKey)`: nothing moved,
  // `effects` is empty, and it is still a 200.
  applied: z.boolean(),
  sanctionKey: z.string(),
  effects: z.array(SanctionEffect),
});
export type ApplyUserSanctionResult = z.infer<typeof ApplyUserSanctionResult>;

// ── ko-backend#818 — the cross-module risk score (PRD §16.1 / §16.5) ─────────
//
// `GET /admin/users/:id/risk` answers the backend's `UserRiskScore` as is. A
// read: it computes and does not write, so opening the tab leaves no audit row.
export const RiskLevelEnum = z.enum(['LOW', 'ELEVATED', 'HIGH', 'CRITICAL']);
export type RiskLevel = z.infer<typeof RiskLevelEnum>;

// One reason-code's contribution. `source` is the module the signal came from
// (`trade`, `creator`, `referral`, `marketplace`, `koins`, `fingerprint`);
// `points` is `min(count × weight, cap)`, so it is what actually added up.
export const RiskContribution = z.object({
  reason: z.string(),
  source: z.string(),
  count: z.number(),
  weight: z.number(),
  points: z.number(),
});
export type RiskContribution = z.infer<typeof RiskContribution>;

export const UserRiskScore = z.object({
  userId: z.string(),
  score: z.number(),
  level: RiskLevelEnum,
  // The weight table that produced the score; a re-weight bumps it, so two
  // scores are only comparable when this matches.
  modelVersion: z.string(),
  windowDays: z.number(),
  // Sorted by `points` desc by the backend.
  contributions: z.array(RiskContribution),
  computedAt: z.string(),
});
export type UserRiskScore = z.infer<typeof UserRiskScore>;
